import {
    IEVDCValidationDetails,
    IFullHPDevice,
} from "../../../services/middleware/hooks/deviceRegisters/useGetFullDevice"

// EVDC Assessment
export const WORST_CASE_POWER_LEVEL = "worstCasePowerLevel"
export const APPLICABLE_STANDARD = "applicableStandard"
export const SSC_MIN_3_PHASE = "sscMin3Phase"
export const CHARGER_SYSTEM_RATED_LE_75 = "chargerSystemRatedLe75"
export const CHARGER_SYSTEM_RATED_GE_75 = "chargerSystemRatedGe75"
export const INFORMATION_PROVIDED = "informationProvided"
export const IN_ACCORDANCE_WITH_EN_61000_3_11 = "inAccordanceWithEn61000311"
export const ZMAX = "zmax"
export const COMPLIANCE_STATUS = "complianceStatus"

const WHOLE_SYSTEM_TESTED =
    "Whole system (> 75A) tested in accordance with IEC 61000-3-11"
const SYSTEM_MODULES_TESTED =
    "System modules (< 75A) tested in accordance with IEC 61000-3-11"
const NONE_STATED = "None stated"
export const EV_DC_VOLTAGE_FLUCTUATION_AND_FLICKER_STATUSES = {
    [WHOLE_SYSTEM_TESTED]: WHOLE_SYSTEM_TESTED,
    [SYSTEM_MODULES_TESTED]: SYSTEM_MODULES_TESTED,
    [NONE_STATED]: NONE_STATED,
}

const formatInformationProvided = (
    providedInfo: string | null
): string | null => {
    if (
        providedInfo &&
        Object.keys(EV_DC_VOLTAGE_FLUCTUATION_AND_FLICKER_STATUSES).includes(
            providedInfo
        )
    ) {
        return providedInfo
    }
    return null
}

export const mapRegisterNameToEVDCValues = (
    data: IEVDCValidationDetails
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [IN_ACCORDANCE_WITH_EN_61000_3_11]:
        data[
            "In Accordance With EN61000-3-11 for Voltage Fluctuation and Flicker"
        ],
    [WORST_CASE_POWER_LEVEL]: data["Worst Case Power Level %"],
    [APPLICABLE_STANDARD]: data["Applicable Harmonic Emission Standard"],
    [SSC_MIN_3_PHASE]: data["SSC Minimum 3 Phase (kVA)"],
    [CHARGER_SYSTEM_RATED_LE_75]:
        data["Charger System Rated Less Than or Equal to 75A (Ω)"],
    [CHARGER_SYSTEM_RATED_GE_75]:
        data["Charger System Rated Greater Than or Equal to 75A (Ω)"],
    [INFORMATION_PROVIDED]: formatInformationProvided(
        data["Information Provided on Voltage Fluctuation and Flicker"]
    ),
    [ZMAX]: data["Maximum Impedance for Voltage Fluctuation and Flicker"],
})

const COMPLIANT_WITH_IEC = "Equipment complying with IEC 61000-3-12"
const COMPLIANT_WITH_IEC_SUBJECT_TO_MINIMUM_POWER =
    "Equipment complying with IEC 61000-3-12 subject to minimum short-circuit power"
const NOT_APPLICABLE_OUTSIDE_SOURCE = "Not applicable (outside scope)"

export const EV_DC_APPLICABLE_STANDARD_STATUSES = {
    [COMPLIANT_WITH_IEC]: COMPLIANT_WITH_IEC,
    [COMPLIANT_WITH_IEC_SUBJECT_TO_MINIMUM_POWER]:
        COMPLIANT_WITH_IEC_SUBJECT_TO_MINIMUM_POWER,
    [NOT_APPLICABLE_OUTSIDE_SOURCE]: NOT_APPLICABLE_OUTSIDE_SOURCE,
}

const COMPLIANT_WITH_IEC_TECHNICAL =
    "Equipment complying with IEC 61000-3-3 technical requirements"
const REQUIRES_ZSOURCE = "Requires Zsource ≤ Zmax"
const REQUIRES_SERVICE_CURRENT_CAPACITY =
    "Requires Service Current Capacity ≥ 100A per phase"
const NOT_APPLICABLE = "Not applicable"

export const EV_DC_VOLTAGE_IN_ACCORDANCE = {
    [COMPLIANT_WITH_IEC_TECHNICAL]: COMPLIANT_WITH_IEC_TECHNICAL,
    [REQUIRES_ZSOURCE]: REQUIRES_ZSOURCE,
    [REQUIRES_SERVICE_CURRENT_CAPACITY]: REQUIRES_SERVICE_CURRENT_CAPACITY,
    [NOT_APPLICABLE]: NOT_APPLICABLE,
}

// Heat Pump Assessment
export const CLASS_A_LIMITS = "classALimits"
export const TECHNICAL_REQUIREMENTS = "technicalRequirements"
export const BOTH_STANDARDS = "bothStandards"
export const TOTAL_SYSTEM_MAX_DEMAND = "totalSystemMaxDemand"
export const CONNECT_NOTIFY = "connectNotify"
export const APPLY_CONNECT = "applyConnect"
export const IN_ACCORDANCE_WITH_EN_61000_3_12 = "inAccordanceWithEn61000312"
export const SSC_MIN_SINGLE_PHASE = "sscMinSinglePhase"
export const MIN_RSCE_CALCULATED = "minRsceCalculated"
export const FAULT_LEVEL = "faultLevel"
export const IMPEDANCE_AT_PCC = "impedanceAtPcc"

export const BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12 =
    "backUpHeaterInAccordanceWithEn61000312"
export const BACK_UP_HEATER_SSC_MIN_3_PHASE = "backUpHeaterSscMin3Phase"
export const BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11 =
    "backUpHeaterInAccordanceWithEn61000311"
export const BACK_UP_HEATER_ZMAX = "backUpHeaterZmax"

export const BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12 =
    "boostHeaterInAccordanceWithEn61000312"
export const BOOST_HEATER_SSC_MIN_3_PHASE = "boostHeaterSscMin3Phase"
export const BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11 =
    "boostHeaterInAccordanceWithEn61000311"
export const BOOST_HEATER_ZMAX = "boostHeaterZmax"

export const WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12 =
    "waterHeaterInAccordanceWithEn61000312"
export const WATER_HEATER_SSC_MIN_3_PHASE = "waterHeaterSscMin3Phase"
export const WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11 =
    "waterHeaterInAccordanceWithEn61000311"
export const WATER_HEATER_ZMAX = "waterHeaterZmax"

export const HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_12 =
    "heatPumpInAccordanceWithEn61000312"
export const HEAT_PUMP_SSC_MIN_3_PHASE = "heatPumpSscMin3Phase"
export const HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_11 =
    "heatPumpInAccordanceWithEn61000311"
export const HEAT_PUMP_ZMAX = "heatPumpZmax"

const replaceNAWithEmptyString = (value: string | number): string | number =>
    value === "N/A" ? "" : value
export const mapRegisterNameToHPValues = (
    data: IFullHPDevice
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [CLASS_A_LIMITS]:
        data.validation["Class A limits of IEC 61000-3-2 or Zpcc > Zref?"],
    [TECHNICAL_REQUIREMENTS]:
        data.validation["Technical Requirements of IEC 61000-3-3?"],
    [BOTH_STANDARDS]:
        data.validation["Both Standards for All Parts of System?"],
    [TOTAL_SYSTEM_MAX_DEMAND]:
        data.validation["Total System Maximum Demand ≤32A?"],
    [CONNECT_NOTIFY]: data.validation["Connect & Notify?"],
    [APPLY_CONNECT]: data.validation["Apply to Connect?"],
    [IN_ACCORDANCE_WITH_EN_61000_3_12]:
        data.validation["In Accordance With EN61000-3-12"],
    [SSC_MIN_3_PHASE]: replaceNAWithEmptyString(
        data.validation["SSC Minimum 3 Phase (kVA)"]
    ),
    [SSC_MIN_SINGLE_PHASE]: replaceNAWithEmptyString(
        data.validation["SSC Minimum Single Phase (kVA)"]
    ),
    [MIN_RSCE_CALCULATED]: replaceNAWithEmptyString(
        data.validation["Minimum Rsce calculated"]
    ),
    [FAULT_LEVEL]: replaceNAWithEmptyString(
        data.validation["Fault level calculated using minimum Rsce (MVA)"]
    ),
    [IMPEDANCE_AT_PCC]: replaceNAWithEmptyString(
        data.validation["Impedance at PCC (Ω)"]
    ),
    [IN_ACCORDANCE_WITH_EN_61000_3_11]:
        data.validation["In Accordance With EN61000-3-11"],
    [ZMAX]: replaceNAWithEmptyString(data.validation["Zmax (Ω)"]),
    [BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12]: replaceNAWithEmptyString(
        data.backUpHeater["In Accordance With EN61000-3-12"]
    ),
    [BACK_UP_HEATER_SSC_MIN_3_PHASE]: replaceNAWithEmptyString(
        data.backUpHeater["SSC Minimum 3 Phase (kVA)"]
    ),
    [BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11]: replaceNAWithEmptyString(
        data.backUpHeater["In Accordance With EN61000-3-11"]
    ),
    [BACK_UP_HEATER_ZMAX]: replaceNAWithEmptyString(
        data.backUpHeater["Zmax (Ω)"]
    ),
    [BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12]: replaceNAWithEmptyString(
        data.boostHeater["In Accordance With EN61000-3-12"]
    ),
    [BOOST_HEATER_SSC_MIN_3_PHASE]: replaceNAWithEmptyString(
        data.boostHeater["SSC Minimum 3 Phase (kVA)"]
    ),
    [BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11]: replaceNAWithEmptyString(
        data.boostHeater["In Accordance With EN61000-3-11"]
    ),
    [BOOST_HEATER_ZMAX]: replaceNAWithEmptyString(data.boostHeater["Zmax (Ω)"]),
    [WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12]: replaceNAWithEmptyString(
        data.waterHeater["In Accordance With EN61000-3-12"]
    ),
    [WATER_HEATER_SSC_MIN_3_PHASE]: replaceNAWithEmptyString(
        data.waterHeater["SSC Minimum 3 Phase (kVA)"]
    ),
    [WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11]: replaceNAWithEmptyString(
        data.waterHeater["In Accordance With EN61000-3-11"]
    ),
    [WATER_HEATER_ZMAX]: replaceNAWithEmptyString(data.waterHeater["Zmax (Ω)"]),
    [HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_12]: replaceNAWithEmptyString(
        data.heatPump["In Accordance With EN61000-3-12"]
    ),
    [HEAT_PUMP_SSC_MIN_3_PHASE]: replaceNAWithEmptyString(
        data.heatPump["SSC Minimum 3 Phase (kVA)"]
    ),
    [HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_11]: replaceNAWithEmptyString(
        data.heatPump["In Accordance With EN61000-3-11"]
    ),
    [HEAT_PUMP_ZMAX]: replaceNAWithEmptyString(data.heatPump["Zmax (Ω)"]),
})

export const BOOLEAN_RADIO_GROUPS = {
    True: "TRUE",
    False: "FALSE",
}

const HP_COMPLYING_12 = "Equipment complying with IEC 61000-3-12"
const HP_COMPLYING_12_SUBJECT_TO_POWER =
    "Equipment complying with IEC 61000-3-12 subject to minimum short-circuit power"
const HP_NON_APPLICABLE = "N/A"

export const HP_IN_ACCORDANCE_12_STATUSES = {
    [HP_COMPLYING_12]: HP_COMPLYING_12,
    [HP_COMPLYING_12_SUBJECT_TO_POWER]: HP_COMPLYING_12_SUBJECT_TO_POWER,
    [HP_NON_APPLICABLE]: HP_NON_APPLICABLE,
}

export const HP_IN_ACCORDANCE_11_STATUSES = {
    [COMPLIANT_WITH_IEC_TECHNICAL]: COMPLIANT_WITH_IEC_TECHNICAL,
    [REQUIRES_ZSOURCE]: REQUIRES_ZSOURCE,
    [REQUIRES_SERVICE_CURRENT_CAPACITY]: REQUIRES_SERVICE_CURRENT_CAPACITY,
    [HP_NON_APPLICABLE]: HP_NON_APPLICABLE,
}
